import {
  ConfirmationDialog,
  dateTime,
  DetailColumns,
  DetailPane,
  FormDialog,
  useDialogs,
  useErrors
} from '@management-ui/core';
import {Delete, Edit, RestoreFromTrash} from '@mui/icons-material';
import {reverse} from 'named-urls';
import * as React from 'react';
import {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useRouteMatch} from 'react-router';
import {useHistory} from 'react-router-dom';
import Materials from '../../../../components/Materials';
import {ServiceContext} from '../../../../components/Services';
import Text from '../../../../components/Text';
import routes from '../../../../routes';
import ContactForm from '../../forms/ContactForm';
import Tabs from './Tabs';
import Wrapper from './Wrapper';

const Contact = ({archive = false}) => {
  const services = useContext(ServiceContext);
  const history = useHistory();
  const match = useRouteMatch();

  const {errors, toggleError} = useErrors(
    useMemo(() => ({contact: 'Sorry the contact could not be accessed'}), [])
  );

  const {openDialogs, toggleDialog} = useDialogs(['contact', 'account', 'delete', 'restore']);

  const idRef = useRef('');
  const [loading, setLoading] = useState(false);
  const [contact, setContact] = useState(null);

  const loadContact = useCallback((id) => {
    if (idRef.current !== `${id}`) {
      idRef.current = `${id}`;
      setLoading(true);
      services.contact.getContact(id, archive).then(retrieved => {
        setContact(retrieved);
        setLoading(false);
      }).catch(() => {
        toggleError('contact', true);
        setLoading(false);
        setTimeout(() => {
          history.push(archive ? reverse(routes.archive.index, {tab: 'contacts'}) : routes.contacts.index);
        }, 3000);
      });
    }
  }, [archive, services, history, toggleError]);

  useEffect(() => {
    if (match.params?.id) {
      loadContact(match.params?.id);
    }
  }, [history, match, contact, loadContact]);

  const [title, setTitle] = useState('Loading');
  const [crumbs, setCrumbs] = useState([...(archive ? Wrapper.archiveCrumbs : Wrapper.standardCrumbs)]);

  useEffect(() => {
    let header = 'Loading';
    let trail = [];
    if (contact) {
      header = contact.name;
      trail = [
        ...(contact.archived ? Wrapper.archiveCrumbs : Wrapper.standardCrumbs),
        {title: header, link: ''}
      ];
    }
    setTitle(header);
    setCrumbs(trail);
  }, [contact]);

  const handleSaved = useCallback((updated) => {
    toggleDialog('contact', false);
    toggleDialog('account', false);
    if (updated) {
      setContact(updated);
    } else if (contact?.id) {
      loadContact(contact.id);
    }
  }, [toggleDialog, setContact, loadContact, contact]);

  const handleDelete = useCallback((confirmed) => {
    toggleDialog('delete', false);
    if (confirmed) {
      setLoading(true);
      services.contact.deleteContact(contact).then(() => {
        setLoading(false);
        history.push(routes.contacts.index);
      }).catch(() => setLoading(false));
    }
  }, [services, history, toggleDialog, contact]);

  const handleRestore = useCallback((confirmed) => {
    toggleDialog('restore', false);
    if (confirmed) {
      setLoading(true);
      services.contact.restoreContact(contact).then((restored) => {
        setLoading(false);
        history.push(reverse(routes.contacts.tab, {id: contact.id, tab: 'locations'}));
        setContact(restored);
      }).catch(() => setLoading(false));
    }
  }, [services, history, toggleDialog, contact]);

  return (
    <Wrapper title={title} contact={contact} loading={loading} crumbs={crumbs} errors={errors}>
      {contact?.id ? (
        <>
          <DetailColumns columns={[

            <DetailPane
              title="Contact Details"
              actions={contact.archived ? [
                {title: 'Restore', icon: <RestoreFromTrash/>, onClick: () => toggleDialog('restore', true)},
              ] : [
                {title: 'Edit', icon: <Edit/>, onClick: () => toggleDialog('contact', true)},
                {title: 'Delete', icon: <Delete/>, onClick: () => toggleDialog('delete', true)},
              ]}
              details={[
                {title: 'Name', value: contact.name},
                {title: 'Customer Code', value: contact.customer_code ?? '-'},
                {title: 'Supplier Code', value: contact.supplier_code ?? '-'},
                {title: 'EORI Number', value: contact.eori ?? '-'},
                {title: 'Website', value: contact.website ?? '-'},
                {title: 'Materials', value: <Materials materials={contact.materials}/>}
              ]}
              dialogs={[
                (props) => (
                  <FormDialog
                    {...props}
                    title="Edit"
                    open={openDialogs['contact'] ?? false}
                    onClose={() => toggleDialog('contact', false)}
                    render={(props) => (
                      <ContactForm
                        {...props}
                        contact={contact}
                        fields="core"
                        onSaved={handleSaved}/>
                    )}
                  />
                ),
                (props) => (
                  <ConfirmationDialog
                    {...props}
                    open={openDialogs['delete'] ?? false}
                    title="Delete Contact"
                    message="Are you sure you want to delete this contact?"
                    onClose={handleDelete}
                  />
                ),
                (props) => (
                  <ConfirmationDialog
                    {...props}
                    open={openDialogs['restore'] ?? false}
                    title="Restore Contact"
                    message="Are you sure you want to restore this contact?"
                    onClose={handleRestore}
                  />
                )
              ]}
            />,

            <DetailPane
              title="Account Details"
              actions={contact.archived ? [] : [
                {title: 'Edit', icon: <Edit/>, onClick: () => toggleDialog('account', true)}
              ]}
              details={[
                {title: 'Company Number', value: contact.company_number ?? '-'},
                {title: 'VAT Number', value: contact.vat_number ?? '-'},
                {title: 'Years Trading', value: contact.years_trading ?? '-'},
                {title: 'Bank Name', value: contact.bank_name ?? '-'},
                {title: 'Bank Address', value: <Text text={contact.bank_address ?? '-'}/>},
                {title: 'Account Number', value: contact.bank_account_number ?? '-'},
                {title: 'Sort Code', value: contact.bank_sort_code ?? '-'},
              ]}
              dialogs={[
                (props) => (
                  <FormDialog
                    {...props}
                    title="Edit Account Details"
                    open={openDialogs['account'] ?? false}
                    onClose={() => toggleDialog('account', false)}
                    maxWidth="md"
                    render={(props) => (
                      <ContactForm
                        {...props}
                        contact={contact}
                        fields="account"
                        onSaved={handleSaved}/>
                    )}
                  />
                )
              ]}
            />,

            <DetailPane
              title="Admin Details"
              details={[
                {title: 'Created', value: dateTime(contact.created_at)},
                {title: 'Last Updated', value: dateTime(contact.updated_at)}
              ]}
            />,
          ]}/>

          <Tabs contact={contact} onContactUpdated={setContact}/>
        </>
      ) : null}
    </Wrapper>
  );
};

export default Contact;
